import { type FC, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Box, Flex, Spinner, Text, useBoolean } from '@chakra-ui/react';
import { forEach, groupBy } from 'lodash';
import { addMinutes } from 'date-fns';
import TripReplaysHeaderSection from './TripReplaysHeaderSection';
import TripCard from './TripCard';
import NoTripsFound from './NoTripsFound';
import BarChartDatePicker from '../../../BarChartDatePicker/BarChartDatePicker';
import {
  Event,
  Feature,
  type Page,
  type Trip,
  type TripGroup,
  type TripJobPair,
  TripsPanelState,
} from '../../../../types';
import { Timestamp } from '../../../../services/Timestamp';
import { type OperationViewModel } from '../../../Map/types';
import { useEcoRatingTooltip } from '../../hooks/useEcoRatingTooltip';
import { getEventFactory } from '../../../../utils/get-event-factory';
import { useTripFilters } from '../../../GlobalContextProvider/hooks/useTripFilters';
import { useDispatch, useTripsContext } from '../../../GlobalContextProvider/GlobalContextProvider';
import { useAppInsights } from '../../../../hooks/useAppInsights';
import { useDetectMobileScreenWidth } from '../../../../hooks/useDetectMobileScreenWidth';
import { useDetectScrolledToBottom } from '../../hooks/useDetectScrolledToBottom';
import { useTripsInfiniteQuery } from '../../../../hooks/queries/useTripsInfiniteQuery';
import { getLocalTimestampFromDate } from '../../../../utils/date-utils';
import { getOldAndNewFilters } from '../../../GlobalContextProvider/utils/filters-utils';
import { ActionType } from '../../../GlobalContextProvider/global-reducer';
import { defaultFilters, initStateFilters } from '../../../GlobalContextProvider/initial-trips-state';
import { defaultTripsPageNumber, quantityOfTripsPerRequest } from '../../constants';

interface Props {
  page: Page;
  onClose: () => void;
  setStartTimestamp: (timestamp: Timestamp) => void;
  setTimestamp: (timestamp: Timestamp) => void;
  onTargetVesselChange: (mmsi: string, vesselName: string) => void;
  setHoveredJob: (tripJobPair: TripJobPair) => void;
  operation: OperationViewModel;
}

const defaultSelectedDateValue = null;

export const TripReplays: FC<Props> = ({
  page,
  onClose,
  setStartTimestamp,
  setTimestamp,
  onTargetVesselChange,
  setHoveredJob,
  operation,
}) => {
  const [displayMoreFilters, setDisplayMoreFilters] = useBoolean();
  const filters = useTripFilters();
  const context = useTripsContext();
  const { endDate, port, tripsPanelState, vesselName } = context;
  const tripsDispatch = useDispatch();
  const appInsights = useAppInsights();
  const timeoutId = useRef<NodeJS.Timeout>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(defaultSelectedDateValue);
  const isMobileView = useDetectMobileScreenWidth();

  const scrollRef = useRef<HTMLDivElement>(undefined);
  const { isBottom } = useDetectScrolledToBottom(scrollRef);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useTripsInfiniteQuery(
    filters,
    quantityOfTripsPerRequest,
    defaultTripsPageNumber,
    page
  );

  useEffect(() => {
    if (isBottom && hasNextPage && !isFetching && trips.length) {
      const event = getEventFactory(Event.TripsPanelScrolledToEnd)(page, port);
      appInsights.trackAnalyticsEvent(event);
      void fetchNextPage();
    }
  }, [isBottom]);

  useEffect(() => {
    if (isFetchingNextPage) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    setSelectedDate(null);
  }, [operation]);

  const setDateFilter = useCallback(
    (date: Date): void => {
      if (date) {
        date.setHours(0, 0, 0);
        const startDate = getLocalTimestampFromDate(date, operation?.timezone).toDate().toISOString();
        date.setHours(23, 59, 59);
        const endDate = getLocalTimestampFromDate(date, operation?.timezone).toDate().toISOString();
        const filters = getOldAndNewFilters(context, { startDate, endDate });
        tripsDispatch({ type: ActionType.UPDATE_START_DATE, payload: startDate });
        tripsDispatch({ type: ActionType.UPDATE_END_DATE, payload: endDate });
        appInsights.trackAnalyticsEvent(
          getEventFactory(Event.FilteredDate)(Feature.TripsHistoryPanel, page, filters.old, filters.new, startDate)
        );
      } else {
        const filters = getOldAndNewFilters(context, { endDate: initStateFilters.endDate });
        tripsDispatch({ type: ActionType.UPDATE_END_DATE, payload: initStateFilters.endDate });
        appInsights.trackAnalyticsEvent(
          getEventFactory(Event.FilteredDate)(
            Feature.TripsHistoryPanel,
            page,
            filters.old,
            filters.new,
            filters.new.startDate
          )
        );
      }
    },
    [operation, tripsDispatch, appInsights, filters]
  );

  useEffect(() => {
    if (endDate === defaultFilters.endDate) {
      setSelectedDate(null);
    } else {
      const date = new Date(endDate);
      const utcDate = addMinutes(date, date.getTimezoneOffset());

      setSelectedDate(utcDate);
    }
  }, [endDate]);

  const trips = useMemo(() => {
    return (data?.pages ?? []).reduce<Trip[]>((result, current) => {
      return [...result, ...current.trips];
    }, []);
  }, [data]);

  const resetFilters = useCallback((): void => {
    setSelectedDate(null);
    onTargetVesselChange(null, null);
    tripsDispatch({ type: ActionType.RESET_FILTER });
    const event = getEventFactory(Event.FiltersReset)(Feature.TripsHistoryPanel, page, filters);
    appInsights.trackAnalyticsEvent(event);
  }, []);

  const onJobHovered = useCallback(
    (tripId: string, jobId: string): void => {
      const trip = trips.find(t => t.tripId === tripId);
      const job = trip?.jobs?.find(j => j.jobId === jobId);

      if (!job) {
        return;
      }

      setHoveredJob({ job, trip });
    },
    [timeoutId.current, trips]
  );

  const onJobHoverOut = useCallback((): void => {
    clearTimeout(timeoutId.current);
    timeoutId.current = null;
    setHoveredJob(null);
  }, [timeoutId.current]);

  const ecoRatingTooltip = useEcoRatingTooltip(tripsPanelState === TripsPanelState.Open);

  const handleCloseTripsPanel = useCallback((): void => {
    onClose();
    ecoRatingTooltip.onClose();
  }, []);

  const onJobSelected = useCallback((): void => {
    setDisplayMoreFilters.off();
    if (isMobileView) {
      handleCloseTripsPanel();
    }
  }, [isMobileView]);

  const tripsNodes = useMemo(() => {
    const dictionary = groupBy(trips, t =>
      Timestamp.fromSeconds(t.timestampStart).toLocationDateTimeOffset(operation?.timezone).format('ddd MMM DD YYYY')
    );
    const groupedTrips: TripGroup[] = [];
    forEach(dictionary, (trips, date) => groupedTrips.push({ date, trips }));

    return groupedTrips.map(group => (
      <div key={group.date}>
        <Text as="p" className="trip-group-header">
          {group.date}
        </Text>
        {group.trips.length > 0 &&
          group.trips.map(trip => (
            <TripCard
              key={trip.id}
              trip={trip}
              onJobSelected={onJobSelected}
              onJobHover={onJobHovered}
              onJobHoverOut={onJobHoverOut}
              vesselNameSearchPhrase={vesselName}
              setStartTimestamp={setStartTimestamp}
              setTimestamp={setTimestamp}
              onTargetVesselChange={onTargetVesselChange}
              page={page}
              operation={operation}
            />
          ))}
      </div>
    ));
  }, [trips]);

  const renderTripReplaysContent = (): JSX.Element => {
    if (isFetching && !isFetchingNextPage) {
      return (
        <Flex justifyContent="center">
          <Spinner />
        </Flex>
      );
    }

    if (tripsNodes.length) {
      return (
        <>
          {tripsNodes}
          <Flex className="bottom-spinner" justifyContent="center" hidden={!isFetchingNextPage}>
            <Spinner />
          </Flex>
        </>
      );
    }

    return <NoTripsFound resetFilters={resetFilters} />;
  };

  return (
    <>
      <TripReplaysHeaderSection
        displayMoreFilters={displayMoreFilters}
        toggleMoreFilters={setDisplayMoreFilters.toggle}
        page={page}
        selectedDate={selectedDate}
        resetFilters={resetFilters}
        onClose={onClose}
      />
      <Box className="trips-panel-jobs-list-container" ref={scrollRef} onScroll={setDisplayMoreFilters.off}>
        <BarChartDatePicker
          setDateFilter={setDateFilter}
          selectedDate={selectedDate}
          height={160}
          operation={operation}
          ecoRatingTooltip={ecoRatingTooltip}
        />
        {renderTripReplaysContent()}
      </Box>
    </>
  );
};
